import { SignupData } from '../components/SignupForm/SignupForm';
import { LoginUserData } from '../components/LoginForm/LoginForm';
import { Task, ColumnType } from '../types/types';

// Base API URL
// Use env var when provided; default to backend on :9550. We avoid relying on the dev proxy to prevent 404/HTML responses when the dev server isn't restarted.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9550';

// Interface for token response
interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiryDuration: number;
  tokenType: string;
}

// Interface for organization statistics
interface OrganizationStats {
  totalUsers: number;
  totalTeams: number;
  activeSprints: number;
  completedSprints: number;
  totalSprints: number;
  totalTasks: number;
}

// Interface for organization settings DTO (used by AdminSettings)
export interface OrganizationSettingsDTO {
  name: string;
  description?: string;
  website?: string;
  timezone: string; // e.g., 'UTC'
  maxTeamSize: number; // 1-100
  sessionTimeoutMinutes: number; // 15-480
  slackIntegrationEnabled: boolean;
  jiraIntegrationEnabled: boolean;
  // Optional array of workflow/status column keys (e.g. ['todo','inProgress','deployed','done'])
  workflowStatuses?: ColumnType[];
}

// Support Ticket interfaces
export interface SupportTicketDTO {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "open" | "in-progress" | "resolved" | "closed";
  category: "technical" | "billing" | "account" | "feature-request" | "bug" | "general";
  organizationId: string;
  organizationName: string;
  userEmail: string;
  userName: string;
  assignedTo?: string;
  assignedToName?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  tags: string[];
  attachments: string[];
  comments: TicketCommentDTO[];
}

export interface TicketCommentDTO {
  id: string;
  ticketId: string;
  author: string;
  authorType: "user" | "support" | "system";
  message: string;
  isInternal?: boolean;
  createdAt: string;
  attachments?: string[];
}

// Paginated tickets response for authenticated user
export interface MyTicketsResponse {
  success: boolean;
  data: SupportTicketDTO[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

// Mock database (unchanged from your original)
interface MockDatabase {
  organizations: Array<{
    id: string;
    name: string;
    industry: string;
    size: string;
    website?: string;
    createdAt: Date;
    plan: string;
  }>;
  users: Array<{
    id: string;
    organizationId: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    role: 'admin' | 'member';
    createdAt: Date;
    password: string;
    teamIds: string[];
    emailVerified: boolean;
  }>;
  teams: Array<{
    id: string;
    organizationId: string;
    name: string;
    description: string;
    memberIds: string[];
    adminIds: string[];
    createdAt: Date;
    createdBy: string;
  }>;
  sprints: Array<{
    id: string;
    organizationId: string;
    teamId: string;
    name: string;
    description: string;
    startDate: Date;
    endDate: Date;
    status: 'planning' | 'active' | 'completed';
    createdAt: Date;
    createdBy: string;
  }>;
}

const mockDatabase: MockDatabase = {
  organizations: [],
  users: [],
  teams: [],
  sprints: []
};

// Main API service
export const realAPI = {


async getAllTasksWithSprints(teamId: string | number): Promise<any[]> {
  // Fetch all tasks (backlog and sprints) for a team, flattening into a single array
  const data = await realAPI.apiRequest(`/api/v1/sprints/team/${teamId}/with-tasks`);
  // Flatten all tasks from all sprints and backlog
  return data.flatMap((sprintObj: any) =>
    (sprintObj.tasks || []).map((task: any) => ({
      ...task,
      status: sprintObj.sprintId, // status is either 'backlog' or sprintId
    }))
  );
},

// Public API methods (no authentication required)
public: {
  // Get public plans for landing page
  getPlans: async () => {
    const fullUrl = `${API_BASE_URL}/api/plans`;
    console.log(`🌐 Public API Request: GET ${fullUrl}`);
    
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`📊 Public API Response: GET ${fullUrl} -> ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Public API Error: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("✅ Public plans data received:", data);
      return data;
    } catch (error) {
      console.error('❌ Error fetching public plans:', error);
      throw error;
    }
  },

  // Get public progressive plans for landing page
  getProgressivePlans: async () => {
    const fullUrl = `${API_BASE_URL}/api/v1/super-admin/public/progressive-plans`;
    console.log(`🌐 Public API Request: GET ${fullUrl}`);
    
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`📊 Public API Response: GET ${fullUrl} -> ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Public API Error: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Some proxies/servers return HTML on error; guard against parsing HTML as JSON
      const text = await response.text();
      if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
        console.error('❌ Public API Error: Received HTML instead of JSON');
        throw new Error('Invalid response format (HTML received)');
      }
      const data = JSON.parse(text);
      console.log("✅ Public progressive plans data received:", data);
      return data;
    } catch (error) {
      console.error('❌ Error fetching public progressive plans:', error);
      throw error;
    }
  }
},





  // Get all backlog tasks (not assigned to a sprint)
  getBacklogTasks: async (): Promise<Task[]> => {
    const tasks = await realAPI.apiRequest(`/api/v1/tasks/sprint/backlog`);
    return tasks.map((task: any) => ({
      id: task.id,
      title: task.title,
      description: task.description,
      priority: task.priority,
      taskColumn: task.taskColumn || 'todo',
      status: task.status,
      teamId: task.teamId,
      assigneeUserId: task.assigneeUserId,
      storyPoints: task.storyPoints,
      type: task.type || 'story',
      createdAt: task.createdAt ? new Date(task.createdAt) : new Date(),
    }));
  },

  // Update both status and column for a task (for drag-and-drop between backlog/sprints)
  updateTaskStatusAndColumn: async (taskId: string | number, status: string, taskColumn: string | null) => {
    return realAPI.apiRequest(`/api/v1/tasks/${taskId.toString()}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, taskColumn }),
    });
  },

  // Create a new backlog task
  createBacklogTask: async (taskData: {
    title: string;
    description: string;
    priority: string;
    taskColumn: string;
    teamId: string | number;
    assigneeUserId?: string | null;
    storyPoints?: number;
    type?: string;
    attachments?: Array<{
      originalFilename: string;
      url: string;
    }>;
  }) => {
    // Always set status to 'backlog'
    return realAPI.apiRequest('/api/v1/tasks', {
      method: 'POST',
      body: JSON.stringify({
        ...taskData,
        status: 'backlog',
      }),
    });
  },
  /**
   * Creates a new organization and admin user
   */
  createOrganization: async (signupData: SignupData): Promise<{ success: boolean; organizationId: string; userId: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/organization/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(signupData)
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to create organization: ${error}`);
      }

      const data = await response.json();
      return {
        success: true,
        organizationId: data.organizationId,
        userId: data.userId
      };
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  },

  /**
   * Get organization settings
   */
  getOrganizationSettings: async () => {
    return realAPI.apiRequest('/api/v1/admin/settings');
  },

  /**
   * Update organization settings
   */
  updateOrganizationSettings: async (settings: {
    name?: string;
    description?: string;
    website?: string;
    timezone?: string;
    maxTeamSize?: number;
    sessionTimeoutMinutes?: number;
    slackIntegrationEnabled?: boolean;
    jiraIntegrationEnabled?: boolean;
  }) => {
    return realAPI.apiRequest('/api/v1/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings)
    });
  },

  /**
   * Logs in a user and returns tokens
   */
  loginUser: async (email: string, password: string): Promise<{ 
    accessToken: string;
    refreshToken: string;
    expiryDuration: number;
    tokenType: string;
    user: LoginUserData;
  }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/user/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          deviceInfo: {
            deviceType: 0,
            deviceId: Math.random().toString(),
            notificationToken: Math.random().toString()
          }
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        let message = `Login failed (${response.status})`;
        try {
          const errorData = JSON.parse(errText);
          message = errorData.message || errorData.error || message;
        } catch {
          // errText might be HTML; include a short snippet for context
          message = `${message}: ${errText.slice(0, 200)}`;
        }
        throw new Error(message);
      }

      const rawText = await response.text();
      if (rawText.trim().startsWith('<')) {
        throw new Error('Unexpected HTML response from login endpoint');
      }
      const tokenData: TokenResponse = JSON.parse(rawText);
      
      // Store tokens securely
      localStorage.setItem('accessToken', tokenData.accessToken);
      localStorage.setItem('refreshToken', tokenData.refreshToken);
      
      // Fetch user details with the new token
      const userResponse = await realAPI.apiRequest('/api/v1/users/me');
      
      return {
        ...tokenData,
        user: userResponse
      };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Refreshes the access token using the refresh token
   */
  refreshToken: async (): Promise<TokenResponse> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshToken}`
        }
      });

      if (!response.ok) {
        // If refresh fails, clear tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        throw new Error('Session expired. Please login again.');
      }

      const tokenData: TokenResponse = await response.json();

      // Update stored tokens
      localStorage.setItem('accessToken', tokenData.accessToken);
      if (tokenData.refreshToken) {
        localStorage.setItem('refreshToken', tokenData.refreshToken);
      }

      return tokenData;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  },

  /**
   * Logs out the user by clearing tokens and optionally notifying the server
   */
  logout: async (): Promise<void> => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      // Optionally notify server about logout (if endpoint exists)
      if (accessToken) {
        try {
          await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (logoutError) {
          // Even if server logout fails, we still clear local tokens
          console.warn('Server logout failed, but clearing local tokens:', logoutError);
        }
      }
    } finally {
      // Always clear local storage regardless of server response
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userId');
      localStorage.removeItem('organizationId');
    }
  },

  /**
   * Generic API request wrapper with token refresh handling
   */
  apiRequest: async (url: string, options: RequestInit = {}): Promise<any> => {
    const fullUrl = `${API_BASE_URL}${url}`;
    console.log(`🌐 API Request: ${options.method || 'GET'} ${fullUrl}`);
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      
      // Set default headers, but allow them to be overridden
      const defaultHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      };
      
      const requestOptions = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers, // This will override defaults if specified
        }
      };

      const makeRequest = async (): Promise<Response> => {
        const response = await fetch(fullUrl, requestOptions);
        
        console.log(`📊 API Response: ${options.method || 'GET'} ${fullUrl} -> ${response.status} ${response.statusText}`);
        
        // Check for both 401 (Unauthorized) and 403 (Forbidden) for token issues
        if (response.status === 401 || response.status === 403) {
          console.log("🔄 Token expired, attempting refresh...");
          try {
            // Token might be expired, try to refresh
            const { accessToken: newAccessToken } = await realAPI.refreshToken();
            
            // Update the authorization header with new token
            requestOptions.headers = {
              ...requestOptions.headers,
              'Authorization': `Bearer ${newAccessToken}`
            };
            
            // Retry the original request
            console.log("🔄 Retrying request with new token...");
            const retryResponse = await fetch(fullUrl, requestOptions);
            console.log(`📊 Retry Response: ${options.method || 'GET'} ${fullUrl} -> ${retryResponse.status} ${retryResponse.statusText}`);
            return retryResponse;
          } catch (refreshError) {
            console.error('❌ Token refresh failed:', refreshError);
            // Clear invalid tokens and redirect to login page
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
            throw new Error('Session expired. Please log in again.');
          }
        }
        
        return response;
      };

      const response = await makeRequest();
      
      if (!response.ok) {
        // Check if response has content before trying to parse JSON
        const contentType = response.headers.get('content-type');
        let errorMessage = `Request failed with status ${response.status} (${response.statusText})`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            console.error(`❌ API Error [${response.status}]:`, errorData);
            
            // Log additional context for 500 errors
            if (response.status === 500) {
              console.error("💥 Server Error Details:", {
                url: fullUrl,
                method: options.method || 'GET',
                status: response.status,
                statusText: response.statusText,
                errorData,
                timestamp: new Date().toISOString()
              });
            }
            
            throw new Error(errorMessage);
          } catch (jsonError) {
            console.error(`❌ API Error [${response.status}]: Unable to parse error response`, jsonError);
            throw new Error(errorMessage);
          }
        } else {
          console.error(`❌ API Error [${response.status}]: ${errorMessage}`);
          throw new Error(errorMessage);
        }
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        console.log(`✅ API Success: ${options.method || 'GET'} ${fullUrl}`, data);
        return data;
      } else {
        // For non-JSON responses (like 204 No Content), return null
        console.log(`✅ API Success: ${options.method || 'GET'} ${fullUrl} (No content)`);
        return null;
      }
    } catch (error) {
      console.error(`💥 API Request Failed: ${options.method || 'GET'} ${fullUrl}`, {
        error,
        timestamp: new Date().toISOString(),
        method: options.method || 'GET',
        url: fullUrl
      });
      throw error;
    }
  },

  /**
   * Verifies user account with token
   */
  verifyAccount: async (token: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9550';
      const response = await fetch(`${API_BASE_URL}/verify-email?code=${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Verification failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Verification error:', error);
      throw error;
    }
  },

  // Team Management Functions
  createTeam: async (
    organizationId: string,
    teamData: {
      name: string;
      description: string;
      createdBy: string;
    }
  ) => {
    return realAPI.apiRequest('/api/v1/teams', {
      method: 'POST',
      body: JSON.stringify({
        name: teamData.name,
        description: teamData.description,
        members: [],
        organizationId: organizationId,
        createdBy: teamData.createdBy,
      }),
    });
  },

  updateTeam: async (
    teamId: string,
    teamData: {
      name?: string;
      description?: string;
      enabled?: boolean;
    }
  ) => {
    return realAPI.apiRequest(`/api/v1/teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(teamData),
    });
  },

  getTeamsByOrganization: async (organizationId: string) => {
    return realAPI.apiRequest('/api/v1/teams/organization');
  },

  addUserToTeam: async (teamId: string, userId: string) => {
    return realAPI.apiRequest(`/api/v1/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  },

  removeUserFromTeam: async (teamId: string, userId: string) => {
    return realAPI.apiRequest(`/api/v1/teams/${teamId}/members/${userId}`, {
      method: 'DELETE',
    });
  },

  // Sprint Management Functions
  createSprint: async (sprintData: {
    organizationId: string;
    teamId: string;
    name: string;
    description: string;
    startDate: Date;
    endDate: Date;
    createdBy: string;
  }) => {
    return realAPI.apiRequest('/api/v1/sprints', {
      method: 'POST',
      body: JSON.stringify({
        name: sprintData.name,
        description: sprintData.description,
        startDate: sprintData.startDate,
        endDate: sprintData.endDate,
        status: 'planning',
        team: { id: sprintData.teamId },
        organizationId: sprintData.organizationId,
        createdBy: sprintData.createdBy,
      }),
    });
  },

  getSprintsByTeam: async (teamId: string) => {
    return realAPI.apiRequest(`/api/v1/sprints/team/${teamId}`);
  },

  getSprintsByOrganization: async (organizationId: string) => {
    return realAPI.apiRequest('/api/v1/sprints/organization');
  },

  // Get organization statistics
  getOrganizationStats: async (organizationId: string): Promise<OrganizationStats> => {
    return realAPI.apiRequest(`/api/v1/organization-stats/${organizationId}`);
  },

  updateSprintStatus: async (sprintId: string, status: 'planning' | 'active' | 'completed') => {
    return realAPI.apiRequest(`/api/v1/sprints/${sprintId}/status?status=${status}`, {
      method: 'PATCH',
    });
  },

  // Organization and User Management Functions
  getCurrentOrganization: async () => {
    return realAPI.apiRequest('/api/v1/organization');
  },

  // Progressive plan subscription for organization
  subscribeToProgressivePlan: async (progressivePlanId: number) => {
    return realAPI.apiRequest('/api/v1/organization/progressive-plan/subscribe', {
      method: 'POST',
      body: JSON.stringify({ progressivePlanId })
    });
  },

  // Progressive plan topup for organization
  topupProgressivePlan: async (
    progressivePlanId: number, 
    amount: number, 
    paymentVerification?: {
      // Development/test-mode flag to simulate a successful payment
      testPayment?: boolean;
      paypalOrderId?: string;
      paypalOrderStatus?: string;
      paypalPayerEmail?: string;
      paypalAmount?: string;
      paypalCurrency?: string;
      verification?: string;
      // Optional: nested PayPal order details (as sent by UI for server-side verification)
      paypalOrder?: {
        id: string;
        status: string;
        payer?: {
          email?: string;
          name?: {
            given_name?: string;
            surname?: string;
          } | null;
          payerId?: string;
        } | null;
        amount?: {
          value: string;
          currency: string;
        } | null;
        createTime?: string;
        updateTime?: string;
      };
    }
  ) => {
    const payload = { 
      progressivePlanId, 
      amount,
      ...(paymentVerification && { paymentVerification })
    };
    
    return realAPI.apiRequest('/api/v1/organization/progressive-plan/topup', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  // PayPal server-side integration
  // Create order on backend which creates PayPal order and returns orderID
  createPayPalOrder: async (payload: {
    topupId: string;
    organizationId: number;
    progressivePlanId: number;
    userId: number | string;
    amount: string; // e.g. "50.00"
    currency: string; // e.g. "USD"
  }): Promise<{ orderID: string }> => {
    return realAPI.apiRequest('/api/paypal/orders', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Capture an approved PayPal order on backend and grant credits
  capturePayPalOrder: async (
    orderId: string,
    payload: {
      expectedAmount: string; // e.g. "50.00"
      expectedCurrency: string; // e.g. "USD"
      topupId?: string;
      progressivePlanId?: number;
      creditAmount?: number;
    }
  ): Promise<{
    ok: boolean;
    captureId?: string;
    status?: string;
    amount?: string;
    currency?: string;
    error?: string;
  }> => {
    return realAPI.apiRequest(`/api/paypal/orders/${orderId}/capture`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Get available progressive plans (public endpoint)
  getAvailableProgressivePlans: async () => {
    const fullUrl = `${API_BASE_URL}/api/v1/super-admin/public/progressive-plans`;
    console.log(`🌐 Public API Request: GET ${fullUrl}`);
    
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`📊 Public API Response: GET ${fullUrl} -> ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Public API Error: ${response.status} ${response.statusText}`, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("✅ Progressive plans data received:", data);
      return data;
    } catch (error) {
      console.error('❌ Error fetching progressive plans:', error);
      throw error;
    }
  },
  
  getUsersByOrganization: async (organizationId: string) => {
    const orgData = await realAPI.apiRequest('/api/v1/organization');
    return orgData.users || [];
  },

  createUser: async (userData: {
    organizationId: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string;
    role: "ADMIN" | "MEMBER";
    password: string;
    createdBy: string;
  }) => {
    return realAPI.apiRequest('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify({
        ...userData,
        createdAt: new Date().toISOString(),
        emailVerified: true,
        teamIds: [],
      }),
    });
  },

  // Get organization details
  getOrganization: async (organizationId: string) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const organization = mockDatabase.organizations.find(org => org.id === organizationId);
    if (!organization) throw new Error('Organization not found');
    
    const users = mockDatabase.users.filter(user => user.organizationId === organizationId);
    return { organization, users };
  },

  // Get user details
  getUser: async (userId: string) => {
    await new Promise(resolve => setTimeout(resolve, 300));
    const user = mockDatabase.users.find(u => u.id === userId);
    if (!user) throw new Error('User not found');
    return user;
  },

  // Get user settings
  getUserSettings: async () => {
    return realAPI.apiRequest('/api/user-settings/me');
  },

  // Update user settings (email and browser notifications)
  updateUserSettings: async (settings: { emailNotifications: boolean; browserNotifications: boolean }) => {
    return realAPI.apiRequest('/api/user-settings/me', {
      method: 'PATCH',
      body: JSON.stringify(settings)
    });
  },

  // Update email notification preferences (legacy - use updateUserSettings instead)
  updateEmailNotifications: async (emailNotifications: boolean) => {
    return realAPI.updateUserSettings({
      emailNotifications,
      browserNotifications: true // Default to true since we can't get current value in this method
    });
  },

  // Task Management Functions
  createTask: async (taskData: {
    title: string;
    description: string;
    priority: string;
    taskColumn: string;
    status: string;
    teamId: string | number;
    assigneeUserId?: string | null;
    storyPoints?: number;
    type?: string;
    attachments?: Array<{
      originalFilename: string;
      url: string;
      thumbnailUrl?: string;
    }>;
  }) => {
    return realAPI.apiRequest('/api/v1/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
  },

  getTasksBySprintId: async (sprintId: string): Promise<Task[]> => {
    const tasks = await realAPI.apiRequest(`/api/v1/tasks/sprint/${sprintId}`);
    return tasks.map((task: any) => ({
      id: task.id,
      title: task.title,
      description: task.description,
      priority: task.priority,
      taskColumn: task.taskColumn || 'todo',
      status: task.status,
      teamId: task.teamId,
      assigneeUserId: task.assigneeUserId,
      storyPoints: task.storyPoints,
      type: task.type || 'story',
      createdByUserName: task.createdByUserName || null,
      createdAt: task.createdAt ? new Date(task.createdAt) : new Date(),
      updatedAt: task.updatedAt ? new Date(task.updatedAt) : new Date(),
      attachmentUrls: task.attachmentUrls || [], // Legacy format
      attachments: task.attachments || [], // New format with objects
    }));
  },

  // Get a single task by ID
  getTaskById: async (taskId: string | number): Promise<Task> => {
    const task = await realAPI.apiRequest(`/api/v1/tasks/${taskId}`);
    return {
      id: task.id,
      title: task.title,
      description: task.description,
      priority: task.priority,
      taskColumn: task.taskColumn || 'todo',
      status: task.status,
      teamId: task.teamId,
      assigneeUserId: task.assigneeUserId,
      storyPoints: task.storyPoints,
      type: task.type || 'story',
      createdByUserName: task.createdByUserName || null,
      createdAt: task.createdAt, // Keep as string
      updatedAt: task.updatedAt, // Keep as string
      attachmentUrls: task.attachmentUrls || [], // Legacy format
      attachments: task.attachments || [], // New format with objects
    };
  },

  updateTaskColumn: async (taskId: string, taskColumn: string) => {
    return realAPI.apiRequest(`/api/v1/tasks/${taskId}/column`, {
      method: 'PATCH',
      body: JSON.stringify({ taskColumn }),
    });
  },

  // Update task properties (title, description, priority, storyPoints, etc.)
  updateTask: async (taskId: string | number, updates: {
    title?: string;
    description?: string;
    priority?: string;
    storyPoints?: number;
    type?: string;
    assigneeUserId?: string | null;
  }) => {
    return realAPI.apiRequest(`/api/v1/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  },

  // Upload attachment to task
  uploadTaskAttachment: async (taskId: string | number, file: File) => {
    const formData = new FormData();
    formData.append('files', file); // Changed from 'file' to 'files' to match backend

    const fullUrl = `${API_BASE_URL}/api/v1/tasks/${taskId}/attachments`;
    const accessToken = localStorage.getItem('accessToken');
    
    console.log(`🌐 File Upload: POST ${fullUrl}`);
    
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          // Only set Authorization - let browser set Content-Type for FormData
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData,
      });

      console.log(`📊 Upload Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        let errorDetails = null;
        
        try {
          const errorData = await response.json();
          console.error('❌ Upload failed with error data:', errorData);
          
          // Handle specific error cases
          if (errorData.message && typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (errorData.error && typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          }
          
          // Add error details for better handling in the component
          errorDetails = {
            status: response.status,
            error: errorData.error || 'Upload Error',
            message: errorData.message || errorMessage,
            isMaxSizeExceeded: errorData.message?.toLowerCase().includes('maximum upload size exceeded') || 
                               errorData.message?.toLowerCase().includes('file too large') ||
                               errorData.message?.toLowerCase().includes('size exceeded')
          };
        } catch (parseError) {
          // If we can't parse the response, try to get it as text
          try {
            const errorText = await response.text();
            console.error('❌ Upload failed with text:', errorText);
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            console.error('❌ Could not parse error response');
          }
        }
        
        const error = new Error(errorMessage) as Error & { details?: any };
        error.details = errorDetails;
        throw error;
      }

      const result = await response.json();
      console.log('✅ Upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Upload error:', error);
      throw error;
    }
  },

  // Upload multiple attachments to a specific task
  uploadTaskAttachments: async (taskId: string | number, files: File[]) => {
    const formData = new FormData();
    
    // Append multiple files
    files.forEach(file => {
      formData.append('files', file);
    });

    const fullUrl = `${API_BASE_URL}/api/v1/tasks/${taskId}/attachments`;
    const accessToken = localStorage.getItem('accessToken');
    
    console.log(`🌐 Multiple File Upload to Task: POST ${fullUrl}`, files.map(f => f.name));
    
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          // Only set Authorization - let browser set Content-Type for FormData
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData,
      });

      console.log(`📊 Upload Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        let errorDetails = null;
        
        try {
          const errorData = await response.json();
          console.error('❌ Multiple upload failed with error data:', errorData);
          
          // Handle specific error cases
          if (errorData.message && typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (errorData.error && typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          }
          
          // Add error details for better handling in the component
          errorDetails = {
            status: response.status,
            error: errorData.error || 'Upload Error',
            message: errorData.message || errorMessage,
            isMaxSizeExceeded: errorData.message?.toLowerCase().includes('maximum upload size exceeded') || 
                               errorData.message?.toLowerCase().includes('file too large') ||
                               errorData.message?.toLowerCase().includes('size exceeded')
          };
        } catch (parseError) {
          // If we can't parse the response, try to get it as text
          try {
            const errorText = await response.text();
            console.error('❌ Multiple upload failed with text:', errorText);
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            console.error('❌ Could not parse error response');
          }
        }
        
        const error = new Error(errorMessage) as Error & { details?: any };
        error.details = errorDetails;
        throw error;
      }

      const result = await response.json();
      console.log('✅ Multiple upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Multiple upload error:', error);
      throw error;
    }
  },

  // Generic file upload for attachments (for story creation)
  uploadAttachments: async (files: File[]) => {
    const formData = new FormData();
    
    // Append multiple files
    files.forEach(file => {
      formData.append('files', file);
    });

    const fullUrl = `${API_BASE_URL}/api/v1/attachments/upload`;
    const accessToken = localStorage.getItem('accessToken');
    
    console.log(`🌐 Generic File Upload: POST ${fullUrl}`, files.map(f => f.name));
    
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          // Only set Authorization - let browser set Content-Type for FormData
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData,
      });

      console.log(`📊 Upload Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
        let errorDetails = null;
        
        try {
          const errorData = await response.json();
          console.error('❌ Generic upload failed with error data:', errorData);
          
          // Handle specific error cases
          if (errorData.message && typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (errorData.error && typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          }
          
          // Add error details for better handling in the component
          errorDetails = {
            status: response.status,
            error: errorData.error || 'Upload Error',
            message: errorData.message || errorMessage,
            isMaxSizeExceeded: errorData.message?.toLowerCase().includes('maximum upload size exceeded') || 
                               errorData.message?.toLowerCase().includes('file too large') ||
                               errorData.message?.toLowerCase().includes('size exceeded')
          };
        } catch (parseError) {
          // If we can't parse the response, try to get it as text
          try {
            const errorText = await response.text();
            console.error('❌ Generic upload failed with text:', errorText);
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            console.error('❌ Could not parse error response');
          }
        }
        
        const error = new Error(errorMessage) as Error & { details?: any };
        error.details = errorDetails;
        throw error;
      }

      const result = await response.json();
      console.log('✅ Generic upload successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Generic upload error:', error);
      throw error;
    }
  },

  // Delete attachment from task
  deleteTaskAttachment: async (taskId: string | number, attachmentUrl: string) => {
    const fullUrl = `${API_BASE_URL}/api/v1/tasks/${taskId}/attachments`;
    const accessToken = localStorage.getItem('accessToken');
    
    console.log(`🗑️ Delete Attachment: DELETE ${fullUrl}`, { attachmentUrl });
    
    try {
      const response = await fetch(fullUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ attachmentUrl }),
      });

      console.log(`📊 Delete Response: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        let errorMessage = `Delete failed: ${response.status} ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          console.error('❌ Delete attachment failed with error data:', errorData);
          
          if (errorData.message && typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (errorData.error && typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          try {
            const errorText = await response.text();
            console.error('❌ Delete attachment failed with text:', errorText);
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            console.error('❌ Could not parse error response');
          }
        }
        
        const error = new Error(errorMessage);
        throw error;
      }

      const result = await response.json();
      console.log('✅ Attachment deleted successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ Delete attachment error:', error);
      throw error;
    }
  },

  // Fetch authenticated image/file and return blob URL
  fetchAuthenticatedFile: async (filePath: string): Promise<string> => {
    const accessToken = localStorage.getItem('accessToken');
    
    // Extract just the filename from the path
    // Handle cases like "/uploads/thumb_attachment_123.jpg" or "thumb_attachment_123.jpg"
    const filename = filePath.split('/').pop() || filePath;
    const fullUrl = `${API_BASE_URL}/api/v1/attachments/view/${filename}`;
    
    console.log(`🖼️ Fetching authenticated file:`, {
      originalPath: filePath,
      extractedFilename: filename,
      requestUrl: fullUrl
    });
    
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        console.error(`❌ File fetch failed: ${response.status} ${response.statusText}`);
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      console.log(`✅ File fetched successfully, blob URL created`);
      return blobUrl;
    } catch (error) {
      console.error('❌ Error fetching authenticated file:', error);
      throw error;
    }
  },

  // Build an authenticated download URL (as a blob URL) for attachments
  getAuthenticatedDownloadUrl: async (filePath: string): Promise<string> => {
    const accessToken = localStorage.getItem('accessToken');
    const filename = filePath.split('/').pop() || filePath;
    const fullUrl = `${API_BASE_URL}/api/v1/attachments/view/${encodeURIComponent(filename)}?dl=true`;

    console.log('⬇️ Building authenticated download URL:', {
      originalPath: filePath,
      extractedFilename: filename,
      requestUrl: fullUrl
    });

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    });

    if (!response.ok) {
      throw new Error(`Failed to get download: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  },

  // Password management functions
  changePassword: async (data: { 
    newPassword: string; 
    currentPassword?: string; 
    userId?: string | number 
  }) => {
    const formData = new URLSearchParams();
    formData.append('newPassword', data.newPassword);
    
    // If changing another user's password (admin/super admin capability)
    if (data.userId) {
      formData.append('userId', data.userId.toString());
    } else if (data.currentPassword) {
      // If changing own password, current password is required
      formData.append('currentPassword', data.currentPassword);
    }

    return realAPI.apiRequest('/api/v1/users/change-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString()
    });
  },

  // User management functions
  users: {
    // Get all users in organization (for admin/super admin)
    getUsers: async (organizationId?: string | number, page = 0, size = 10) => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        ...(organizationId && { organizationId: organizationId.toString() })
      });
      return realAPI.apiRequest(`/api/v1/users?${params.toString()}`);
    },

    // Get user by ID
    getUserById: async (id: string | number) => {
      return realAPI.apiRequest(`/api/v1/users/${id}`);
    },

    // Update user details
    updateUser: async (id: string | number, userData: any) => {
      return realAPI.apiRequest(`/api/v1/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(userData)
      });
    },

    // Delete user by ID
    deleteUser: async (id: string | number) => {
      // Backend responds with ApiResponse { success: boolean, message: string }
      return realAPI.apiRequest(`/api/v1/users/${id}`, {
        method: 'DELETE',
      });
    },

    // Change another user's password (admin/super admin only)
    changeUserPassword: async (userId: string | number, newPassword: string) => {
      return realAPI.changePassword({ 
        newPassword, 
        userId 
      });
    },

    // Change own password
    changeOwnPassword: async (currentPassword: string, newPassword: string) => {
      return realAPI.changePassword({ 
        currentPassword, 
        newPassword 
      });
    }
  },

  // Helper functions for mock data (unchanged)
  getAllOrganizations: () => mockDatabase.organizations,
  getAllUsers: () => mockDatabase.users,
  clearDatabase: () => {
    mockDatabase.organizations.length = 0;
    mockDatabase.users.length = 0;
  },

  // Story Comments API
  storyComments: {
    // Get comments for a story
    getComments: async (storyId: string | number) => {
      return realAPI.apiRequest(`/api/stories/${storyId}/comments`);
    },

    // Add a comment to a story
    addComment: async (storyId: string | number, authorId: string | number, text: string) => {
      console.log("📤 API addComment params:", { storyId, authorId, text });
      console.log("🔍 authorId type:", typeof authorId, "value:", authorId);
      
      // Ensure authorId is a valid number and not null/undefined
      const numericAuthorId = parseInt(authorId.toString(), 10);
      if (isNaN(numericAuthorId) || !authorId) {
        console.error("❌ Invalid authorId:", authorId);
        throw new Error('Invalid author ID provided');
      }
      
      const payload = {
        authorId: numericAuthorId,
        text: text
      };
      
      console.log("📦 API payload:", payload);
      
      return realAPI.apiRequest(`/api/stories/${storyId}/comments`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    }
  },

  // Work Logs API
  workLogs: {
    // Get work logs for a task
    getWorkLogs: async (taskId: string | number) => {
      return realAPI.apiRequest(`/api/v1/worklogs/task/${taskId}`);
    }
  },

  // Unauthenticated API request (no Authorization header)
  unauthenticatedRequest: async (url: string, options: RequestInit = {}): Promise<any> => {
    const fullUrl = `${API_BASE_URL}${url}`;
    console.log(`🌐 Unauthenticated API Request: ${options.method || 'GET'} ${fullUrl}`);
    
    try {
      // Set default headers WITHOUT authorization
      const defaultHeaders = {
        'Content-Type': 'application/json'
      };
      
      const requestOptions = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers, // This will override defaults if specified
        }
      };

      const response = await fetch(fullUrl, requestOptions);
      
      console.log(`📊 Unauthenticated API Response: ${options.method || 'GET'} ${fullUrl} -> ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        // Check if response has content before trying to parse JSON
        const contentType = response.headers.get('content-type');
        let errorMessage = `Request failed with status ${response.status} (${response.statusText})`;
        
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            console.error(`❌ Unauthenticated API Error [${response.status}]:`, errorData);
            throw new Error(errorMessage);
          } catch (jsonError) {
            console.error(`❌ Unauthenticated API Error [${response.status}]: Unable to parse error response`, jsonError);
            throw new Error(errorMessage);
          }
        } else {
          console.error(`❌ Unauthenticated API Error [${response.status}]: ${errorMessage}`);
          throw new Error(errorMessage);
        }
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        console.log(`✅ Unauthenticated API Success: ${options.method || 'GET'} ${fullUrl}`, data);
        return data;
      } else {
        // For non-JSON responses (like 204 No Content), return null
        console.log(`✅ Unauthenticated API Success: ${options.method || 'GET'} ${fullUrl} (No content)`);
        return null;
      }
    } catch (error) {
      console.error(`❌ Unauthenticated API Error: ${options.method || 'GET'} ${fullUrl}`, error);
      throw error;
    }
  },
  
  // Password Reset functionality
  forgotPassword: async (email: string) => {
    return realAPI.unauthenticatedRequest('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  resetPassword: async (token: string, password: string) => {
    return realAPI.unauthenticatedRequest('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password })
    });
  },

  validateResetToken: async (token: string) => {
    return realAPI.unauthenticatedRequest(`/api/v1/auth/validate-reset-token?token=${encodeURIComponent(token)}`, {
      method: 'GET'
    });
  },  superAdmin: {
    // Get system metrics and dashboard overview
    getSystemMetrics: async () => {
      return realAPI.apiRequest('/api/v1/super-admin/metrics');
    },

    // Organizations management
    getAllOrganizations: async (page = 0, size = 10, search = '', status = '', planType = '') => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        ...(search && { search }),
        ...(status && { status }),
        ...(planType && { planType })
      });
      return realAPI.apiRequest(`/api/v1/super-admin/organizations?${params.toString()}`);
    },

    getOrganizationById: async (id: string | number) => {
      return realAPI.apiRequest(`/api/v1/super-admin/organizations/${id}`);
    },

    updateOrganizationStatus: async (id: string | number, status: string) => {
      return realAPI.apiRequest(`/api/v1/super-admin/organizations/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      });
    },

    updateOrganization: async (id: string | number, organizationData: any) => {
      return realAPI.apiRequest(`/api/v1/super-admin/organizations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(organizationData)
      });
    },

    createOrganization: async (organizationData: any) => {
      return realAPI.apiRequest('/api/v1/super-admin/organizations', {
        method: 'POST',
        body: JSON.stringify(organizationData)
      });
    },

    deleteOrganization: async (id: string | number) => {
      return realAPI.apiRequest(`/api/v1/super-admin/organizations/${id}`, {
        method: 'DELETE'
      });
    },

    // Progressive Plan Topup
    topupProgressivePlan: async (topupData: {
      organizationId: number;
      planId: number;
      amount: number;
      adminNote: string;
      adminEmail: string;
    }) => {
      return realAPI.apiRequest('/api/v1/super-admin/organization-progressive-plan/topup', {
        method: 'POST',
        body: JSON.stringify(topupData)
      });
    },

    // Plans management
    getAllPlans: async (page = 0, size = 10) => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString()
      });
      return realAPI.apiRequest(`/api/v1/super-admin/plans?${params.toString()}`);
    },

    createPlan: async (planData: any) => {
      return realAPI.apiRequest('/api/v1/super-admin/plans', {
        method: 'POST',
        body: JSON.stringify(planData)
      });
    },

    updatePlan: async (id: string | number, planData: any) => {
      return realAPI.apiRequest(`/api/v1/super-admin/plans/${id}`, {
        method: 'PUT',
        body: JSON.stringify(planData)
      });
    },

    deletePlan: async (id: string | number) => {
      return realAPI.apiRequest(`/api/v1/super-admin/plans/${id}`, {
        method: 'DELETE'
      });
    },

    // Progressive Plans management
    getAllProgressivePlans: async () => {
      return realAPI.apiRequest('/api/v1/super-admin/progressive-plans');
    },

    createProgressivePlan: async (planData: any) => {
      return realAPI.apiRequest('/api/v1/super-admin/progressive-plans', {
        method: 'POST',
        body: JSON.stringify(planData)
      });
    },

    updateProgressivePlan: async (id: string | number, planData: any) => {
      return realAPI.apiRequest(`/api/v1/super-admin/progressive-plans/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(planData)
      });
    },

    deleteProgressivePlan: async (id: string | number) => {
      return realAPI.apiRequest(`/api/v1/super-admin/progressive-plans/${id}`, {
        method: 'DELETE'
      });
    },

    // System settings
    getSystemSettings: async () => {
      return realAPI.apiRequest('/api/v1/super-admin/settings');
    },

    updateSystemSettings: async (settings: any) => {
      return realAPI.apiRequest('/api/v1/super-admin/settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
    },

    // Support Tickets management
    getAllSupportTickets: async (page = 0, size = 50, status = '', priority = '', category = '', search = '') => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        ...(status && { status }),
        ...(priority && { priority }),
        ...(category && { category }),
        ...(search && { search })
      });
      return realAPI.apiRequest(`/api/v1/super-admin/tickets?${params.toString()}`);
    },

    getSupportTicketById: async (id: string | number) => {
      return realAPI.apiRequest(`/api/v1/super-admin/tickets/${id}`);
    },

    createSupportTicket: async (ticketData: any) => {
      return realAPI.apiRequest('/api/v1/super-admin/tickets', {
        method: 'POST',
        body: JSON.stringify(ticketData)
      });
    },

    updateSupportTicket: async (id: string | number, ticketData: any) => {
      return realAPI.apiRequest(`/api/v1/super-admin/tickets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(ticketData)
      });
    },

    updateTicketStatus: async (id: string | number, status: string) => {
      return realAPI.apiRequest(`/api/v1/super-admin/tickets/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      });
    },

    assignTicket: async (id: string | number, assignedTo: string) => {
      return realAPI.apiRequest(`/api/v1/super-admin/tickets/${id}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({ assignedTo })
      });
    },

    addTicketComment: async (
      ticketId: string | number,
      commentData: { message?: string; content?: string; isInternal?: boolean; attachments?: string[] }
    ) => {
      // Backend expects 'content'
      const body = {
        content: commentData.content ?? commentData.message ?? '',
        ...(commentData.isInternal !== undefined ? { isInternal: !!commentData.isInternal } : {}),
        ...(Array.isArray(commentData.attachments) ? { attachments: commentData.attachments } : {}),
      };
      const resp = await realAPI.apiRequest(`/api/v1/super-admin/tickets/${ticketId}/comments`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      const raw = resp?.data || resp;
      const mapped: TicketCommentDTO = {
        id: raw?.id?.toString?.() || raw?.id || String(Date.now()),
        ticketId: raw?.ticketId?.toString?.() || String(ticketId),
        author: raw?.authorName || raw?.author || 'Support',
        authorType: (raw?.authorRole ? String(raw.authorRole) : 'SUPPORT').toLowerCase() === 'support' ? 'support' : 'user',
        message: raw?.content || raw?.message || body.content,
        isInternal: !!raw?.isInternal,
        createdAt: typeof raw?.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
        attachments: Array.isArray(raw?.attachments) ? raw.attachments : body.attachments || [],
      };
      return { success: true, data: mapped } as any;
    },

    getTicketComments: async (ticketId: string | number) => {
      const resp = await realAPI.apiRequest(`/api/v1/super-admin/tickets/${ticketId}/comments`);
      const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const mapped: TicketCommentDTO[] = list.map((c: any) => ({
        id: c.id?.toString?.() || c.id,
        ticketId: c.ticketId?.toString?.() || String(ticketId),
        author: c.authorName || c.author || 'Support',
        authorType: (c.authorRole ? String(c.authorRole) : 'SUPPORT').toLowerCase() === 'support' ? 'support' : 'user',
        message: c.content || c.message || '',
        isInternal: !!c.isInternal,
        createdAt: typeof c.createdAt === 'string' ? c.createdAt : new Date(c.createdAt || Date.now()).toISOString(),
        attachments: Array.isArray(c.attachments) ? c.attachments : [],
  }))
  // Ensure oldest first in UI (new comments last)
  .sort((a: TicketCommentDTO, b: TicketCommentDTO) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return { success: true, data: mapped } as any;
    },

    uploadTicketAttachment: async (ticketId: string | number, file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const fullUrl = `${API_BASE_URL}/api/v1/super-admin/tickets/${ticketId}/attachments`;
      const accessToken = localStorage.getItem('accessToken');
      
      console.log(`🌐 Ticket Attachment Upload: POST ${fullUrl}`);
      
      try {
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          },
          body: formData,
        });

        console.log(`📊 Upload Response: ${response.status} ${response.statusText}`);

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('✅ Ticket attachment upload successful:', result);
        return result;
      } catch (error) {
        console.error('❌ Ticket attachment upload error:', error);
        throw error;
      }
    },

    deleteTicket: async (id: string | number) => {
      return realAPI.apiRequest(`/api/v1/super-admin/tickets/${id}`, {
        method: 'DELETE'
      });
    }
  },

  /**
   * Create a Support Ticket for the authenticated (registered) user
   */
  createSupportTicketRegistered: async (payload: {
    subject: string;
    description?: string;
    supportType: 'TECHNICAL' | 'BILLING' | 'ACCOUNT' | 'FEATURE_REQUEST' | 'BUG' | 'GENERAL';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    category?: 'technical' | 'billing' | 'account' | 'feature-request' | 'bug' | 'general';
    organizationId?: string;
    tags?: string[];
  }) => {
    // Ensure backend-required "category" is present by mapping from supportType when missing
    const mapSupportTypeToCategory = (st: typeof payload.supportType): typeof payload.category => {
      const mapped = st.toLowerCase().replace(/_/g, '-');
      return mapped as typeof payload.category;
    };

    // Coerce organizationId to a number if it's a numeric string (many backends expect a number)
    const normalizeOrganizationId = (orgId?: string): number | undefined => {
      if (!orgId) return undefined;
      const n = Number(orgId);
      return Number.isFinite(n) ? n : undefined;
    };

    // Build category value and normalize to UPPER_SNAKE_CASE for typical Java enums
    const categoryClient = payload.category || mapSupportTypeToCategory(payload.supportType);
    const categoryEnum = categoryClient
      ? categoryClient.replace(/-/g, '_').toUpperCase()
      : undefined;

    // Normalize priority to UPPERCASE enum expected by backend (e.g., MEDIUM)
    const priorityEnum = (payload.priority || 'medium').toUpperCase();

    // Normalize supportType to UPPER_SNAKE_CASE (payload.supportType is already upper-snake from UI, but enforce)
    const supportTypeEnum = payload.supportType.replace(/-/g, '_').toUpperCase();

    // Send only fields the backend is likely to allow; avoid unknown fields like supportType
    const apiPayload = {
      subject: payload.subject,
      description: payload.description,
      category: categoryEnum,
      supportType: supportTypeEnum,
      // Some backends may expect 'type' instead of 'supportType'
      type: supportTypeEnum,
      // Some backends may expect 'support_type' (snake_case)
      support_type: supportTypeEnum,
      // Some may expect 'ticketType'
      ticketType: supportTypeEnum,
      priority: priorityEnum,
      organizationId: normalizeOrganizationId(payload.organizationId),
      tags: Array.isArray(payload.tags) ? payload.tags : [],
    } as {
      subject: string;
      description?: string;
      category?: string;
      supportType: string;
      type: string;
      support_type: string;
      ticketType: string;
      priority: string;
      organizationId?: number;
      tags: string[];
    };

    return realAPI.apiRequest('/api/v1/tickets/registered', {
      method: 'POST',
      body: JSON.stringify(apiPayload)
    });
  },

  /**
   * Get all support tickets for the authenticated user (paginated)
   */
  getMySupportTickets: async (page = 0, size = 10): Promise<MyTicketsResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    return realAPI.apiRequest(`/api/v1/tickets/my?${params.toString()}`);
  },

  /**
   * Get a specific ticket by ID for the authenticated user
   */
  getMyTicketById: async (ticketId: string | number): Promise<{ success: boolean; data: SupportTicketDTO }> => {
    return realAPI.apiRequest(`/api/v1/tickets/${ticketId}`);
  },

  /**
   * Get comments for a specific ticket (owned by the authenticated user)
   */
  getMyTicketComments: async (ticketId: string | number): Promise<{ success: boolean; data: TicketCommentDTO[] }> => {
    const resp = await realAPI.apiRequest(`/api/v1/tickets/${ticketId}/comments`);
    const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
    const mapped: TicketCommentDTO[] = list.map((c: any) => ({
      id: c.id?.toString?.() || c.id,
      ticketId: c.ticketId?.toString?.() || (ticketId as string),
      author: c.authorName || c.author || 'User',
      authorType: (c.authorRole ? String(c.authorRole) : 'USER').toLowerCase() === 'support' ? 'support' : 'user',
      message: c.content || c.message || '',
      isInternal: !!c.isInternal,
      createdAt: typeof c.createdAt === 'string' ? c.createdAt : new Date(c.createdAt || Date.now()).toISOString(),
      attachments: Array.isArray(c.attachments) ? c.attachments : [],
    }))
    // Ensure oldest first in UI (new comments last)
    .sort((a: TicketCommentDTO, b: TicketCommentDTO) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    return { success: true, data: mapped };
  },

  /**
   * Add a comment to a ticket (owned by the authenticated user)
   */
  addMyTicketComment: async (
    ticketId: string | number,
    payload: { message: string; attachments?: string[] }
  ): Promise<{ success: boolean; data: TicketCommentDTO }> => {
    // Backend expects 'content'; map from our 'message'
    const body = {
      content: payload.message,
      ...(payload.attachments ? { attachments: payload.attachments } : {}),
    };
    const resp = await realAPI.apiRequest(`/api/v1/tickets/${ticketId}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const raw = resp?.data || resp;
    const mapped: TicketCommentDTO = {
      id: raw?.id?.toString?.() || raw?.id || crypto.randomUUID?.() || String(Date.now()),
      ticketId: raw?.ticketId?.toString?.() || (ticketId as string),
      author: raw?.authorName || raw?.author || 'User',
      authorType: (raw?.authorRole ? String(raw.authorRole) : 'USER').toLowerCase() === 'support' ? 'support' : 'user',
      message: raw?.content || raw?.message || payload.message,
      isInternal: !!raw?.isInternal,
      createdAt: typeof raw?.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
      attachments: Array.isArray(raw?.attachments) ? raw.attachments : payload.attachments || [],
    };
    return { success: true, data: mapped };
  },
};